import { dispatcher } from 'dispatcher';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { isEmpty, prop, pick } from 'ramda';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'js/hooks/navigate';
import { useTranslations } from 'js/hooks/i18n';
import { Image } from 'js/components/common/Image';
import { LocalPopup } from 'js/components/common/Popup';
import { useGameConfig } from 'js/components/common/Game';
import { storageService } from 'js/services/storage.service';
import { WebContentInner } from 'js/components/system/WebContent';
import { useAppSelector, useUserSelector } from 'js/store/hooks';
import { PlayButton, DemoButton } from 'js/components/common/Game/Buttons';

import style from './LegacyGameInfoPopup.scss';

const popupClassNames = {
    inner: style.popupInner
};

export const LegacyGameInfoPopup = ({ onClose, controls }) => {
    const navigate = useNavigate();
    const { gameCode: urlGameCode } = useParams();
    const gameConfig = useGameConfig(urlGameCode);
    const { rootPage, isGamesLoaded, gamesArticles = {} } = useAppSelector(
        pick(['rootPage', 'isGamesLoaded', 'gamesArticles'])
    );
    const { name, images, gameType, funSupport, articleId, gameCode } = gameConfig;
    const isLoggedIn = useUserSelector(prop('isLoggedIn'));
    const t = useTranslations('playtech.mobile.games-info.gameType.');
    const isShowDemoButton = !(funSupport === 'unavailable' || funSupport === 'guest' && isLoggedIn);
    const description = gamesArticles[articleId];
    const { additionalQueryString } = storageService.session.getItem('gamesPortletPreferences') || {};

    useEffect(() => {
       if (isGamesLoaded && isEmpty(gameConfig)) {
           navigate(rootPage);
       }
    }, [isGamesLoaded, navigate, gameConfig, rootPage]);

    useEffect(() => {
        dispatcher.listen('game:info:close', onClose);

        return () => {
            dispatcher.removeListener('game:info:close', onClose);
        };
    }, [onClose]);

    if (isEmpty(gameConfig)) {
        return null;
    }

    const helmet = (
        <Helmet>
            <title>{name}</title>
        </Helmet>
    );

    return (
        <>
            {helmet}
            <LocalPopup
                onClose={onClose}
                controls={controls}
                className={style.popup}
                classNames={popupClassNames}
            >
                <div className={style.topContainer}>
                    <Image
                        alt={name}
                        className={style.image}
                        src={!isLoggedIn && images?.guest || images?.base}
                    />
                    {name && <div className={style.title}>{name}</div>}
                    {gameType && <div className={style.type}>{t(gameType)}</div>}
                </div>
                <div className={style.buttons}>
                    {isShowDemoButton && (
                        <DemoButton
                            className={`${style.btn} ${style.demoBtn}`}
                            code={gameCode}
                            name={name}
                            isHoverBtn={true}
                            queryString={additionalQueryString}
                        />
                    )}
                    <PlayButton
                        className={`${style.btn} ${style.playBtn}`}
                        code={gameCode}
                        name={name}
                        isHoverBtn={true}
                        queryString={additionalQueryString}
                    />
                </div>
                {description && (
                    <WebContentInner
                        content={description}
                        className={style.description}
                    />
                )}
            </LocalPopup>
        </>
    );
};
